import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';

import TextInput from './ui/TextInput.jsx';

// Shared offboarding handover (devir-teslim) items editor.
// Used by the employee self-service (My Offboarding > Handover) and by the
// HR offboarding-handovers page (fill on behalf). Controlled component:
// the parent owns `items` and receives the full new array via `onChange`.

const ITEM_TYPES = [
  'active_project', 'process', 'stakeholder',
  'critical_doc', 'incomplete_task', 'reference_note',
];

export const HANDOVER_ITEM_TYPES = ITEM_TYPES;

export function parseHandoverItems(itemsJson) {
  if (!itemsJson) return [];
  try {
    const arr = JSON.parse(itemsJson);
    if (!Array.isArray(arr)) return [];
    return arr.map((it, i) => ({
      key: it.key || `item-${i}-${it.type || 'x'}`,
      type: it.type || 'reference_note',
      title: it.title || '',
      description: it.description || '',
      transferToExternalEmail: it.transferToExternalEmail || '',
      status: it.status || 'open',
    }));
  } catch {
    return [];
  }
}

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const ItemCard = styled.div`
  background: #fff;
  border: 1px solid #E8ECF0;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ItemHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const TypeBadge = styled.span`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: 12px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(2, 145, 239, 0.10);
  color: ${({ theme }) => theme.colors.primary.main};
`;

const IconBtn = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.auxiliar.danger};
  display: flex;
  align-items: center;
  padding: 4px;
  border-radius: 6px;
  &:hover { background: rgba(229, 57, 53, 0.08); }
`;

const ReadField = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};

  span {
    display: block;
    font-size: 12px;
    color: ${({ theme }) => theme.colors.text.dark.little};
    margin-bottom: 2px;
  }
`;

const AddRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const AddChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: 13px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  border: 1px dashed ${({ theme }) => theme.colors.primary.main};
  background: rgba(2, 145, 239, 0.04);
  color: ${({ theme }) => theme.colors.primary.main};
  &:hover { background: rgba(2, 145, 239, 0.1); }
`;

const SectionLabel = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin-bottom: 8px;
`;

const Empty = styled.div`
  text-align: center;
  padding: 32px 16px;
  color: ${({ theme }) => theme.colors.text.dark.little};
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
`;

export default function HandoverItemsEditor({ items, onChange, editable = false, emptyText }) {
  const { t } = useTranslation();

  const updateItem = (idx, field, value) =>
    onChange(items.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));

  const removeItem = (idx) => onChange(items.filter((_, i) => i !== idx));

  const addItem = (type) =>
    onChange([
      ...items,
      { key: `item-${Date.now()}`, type, title: '', description: '', transferToExternalEmail: '', status: 'open' },
    ]);

  return (
    <Wrap>
      {items.length === 0 && !editable && (
        <Empty>{emptyText || t('handoverItems.empty')}</Empty>
      )}

      {items.map((item, idx) => (
        <ItemCard key={item.key}>
          <ItemHead>
            <TypeBadge>{t(`handoverItems.itemType.${item.type}`, { defaultValue: item.type })}</TypeBadge>
            {editable && (
              <IconBtn type="button" onClick={() => removeItem(idx)} title={t('common.delete')}>
                <Trash2 size={16} />
              </IconBtn>
            )}
          </ItemHead>

          {editable ? (
            <>
              <TextInput
                label={t('handoverItems.field.title')}
                value={item.title}
                onChange={(e) => updateItem(idx, 'title', e.target.value)}
              />
              <TextInput
                type="textarea"
                rows={2}
                label={t('handoverItems.field.description')}
                value={item.description}
                onChange={(e) => updateItem(idx, 'description', e.target.value)}
              />
              <TextInput
                label={t('handoverItems.field.transferEmail')}
                value={item.transferToExternalEmail}
                onChange={(e) => updateItem(idx, 'transferToExternalEmail', e.target.value)}
              />
            </>
          ) : (
            <>
              <ReadField><span>{t('handoverItems.field.title')}</span>{item.title || '—'}</ReadField>
              {item.description && (
                <ReadField><span>{t('handoverItems.field.description')}</span>{item.description}</ReadField>
              )}
              {item.transferToExternalEmail && (
                <ReadField><span>{t('handoverItems.field.transferEmail')}</span>{item.transferToExternalEmail}</ReadField>
              )}
            </>
          )}
        </ItemCard>
      ))}

      {editable && (
        <div>
          <SectionLabel>{t('handoverItems.addItem')}</SectionLabel>
          <AddRow>
            {ITEM_TYPES.map((type) => (
              <AddChip key={type} type="button" onClick={() => addItem(type)}>
                <Plus size={14} />
                {t(`handoverItems.itemType.${type}`, { defaultValue: type })}
              </AddChip>
            ))}
          </AddRow>
        </div>
      )}
    </Wrap>
  );
}
